/* eslint-disable jsx-a11y/click-events-have-key-events */
/* eslint-disable jsx-a11y/no-static-element-interactions */
import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { OverlayTrigger, Dropdown, Tooltip } from 'react-bootstrap';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { FilterMatchMode } from 'primereact/api';
import classNames from 'classnames';
import refreshIcon from '../../../assets/images/refresh-purple.svg';
import { useExternalAgencyAssignedShiftsQuery } from '../../../redux/externalAgencyShifts';
import { applyFilters } from '../../../utils/FilterFunctions';
import FiltersSection from './FiltersSection';
import SLOT_STATUSES from '../../../constants/slotStatuses';
import {
	shiftTypeTemplate,
	rateTemplate,
	estimatedPayTemplate,
	startDateTemplate,
	timePeriodTemplate,
	facilityTemplate,
	buildFilterOptions,
	DEFAULT_FILTER_OPTIONS,
	idFieldTemplate,
	bundleFlagTemplate
} from './agency-utils';
import CancelShiftModal from './cancel-shift-professionals/CancelShiftModal';
import { showNotification } from '../../../redux/notification';

import blockIcon from '../../../assets/images/block-icon.svg';

const AgencyAssignedShifts = ({ searchTerm }) => {
	const dispatch = useDispatch();
	const [filterOptions, setFilterOptions] = useState(DEFAULT_FILTER_OPTIONS);
	const [filters, setFilters] = useState(DEFAULT_FILTER_OPTIONS);
	const [globalFilter, setGlobalFilter] = useState({ global: { value: null, matchMode: FilterMatchMode.CONTAINS } });
	const [shifts, setShifts] = useState([]);
	const [filteredBundleId, setFilteredBundleId] = useState(null);
	const [showCancelShiftModal, setShowCancelShiftModal] = useState(null);
	const [selectedRow, setSelectedRow] = useState(null);

	const { data: assignedShifts, refetch: reloadAssignedShifts } = useExternalAgencyAssignedShiftsQuery(null, {
		refetchOnMountOrArgChange: true
	});

	useEffect(() => {
		if (assignedShifts) {
			const options = buildFilterOptions(assignedShifts, filters);
			setFilterOptions(options);
		}
	}, [assignedShifts]);

	useEffect(() => {
		let filteredShifts;
		if (filteredBundleId) {
			filteredShifts = assignedShifts.filter((s) => s.contract_id === filteredBundleId);
		} else {
			filteredShifts = applyFilters({ items: assignedShifts || [], filters });
		}
		setShifts(filteredShifts);
	}, [assignedShifts, filters, filteredBundleId]);

	useEffect(() => {
		const _filter = { ...globalFilter };
		_filter.global.value = searchTerm;
		setGlobalFilter(_filter);
	}, [searchTerm]);

	const updateFilters = (updatedOptions, updatedFilters) => {
		setFilterOptions(updatedOptions);
		setFilters(updatedFilters);
	};

	const showRowAsCancelled = (rowData) => {
		return rowData.slot_status === SLOT_STATUSES.FACILITY_CANCELLED || rowData.shift_replacement_reason;
	};

	const rowClass = (rowData) => (showRowAsCancelled(rowData) ? 'cancelled-shift-row' : '');

	const professionalTemplate = (item) => <div className="caregiver-name line-clamp">{item.professional}</div>;
	const unitTemplate = (item) => <div className="unit-name line-clamp">{item.unit}</div>;
	const cityTemplate = (item) => <span>{item.facility_city}</span>;
	const stateTemplate = (item) => <span>{item.facility_state_code}</span>;
	const zipTemplate = (item) => <span>{item.facility_zip_code}</span>;

	const handleCancelShiftModal = (shift) => {
		if (shift.facility_allow_external_worker_shift_replacement && !showRowAsCancelled(shift)) {
			setShowCancelShiftModal(true);
			setSelectedRow(shift);
		}
	};

	const onCancelShiftCompleted = () => {
		setShowCancelShiftModal(false);
		setSelectedRow(null);
		reloadAssignedShifts();
	};

	const actionMenuTemplate = (item) => {
		return (
			<Dropdown
				className={classNames('shift-action-dropdown', {
					disabled: showRowAsCancelled(item)
				})}
			>
				<OverlayTrigger
					trigger={['hover']}
					placement="bottom"
					key="shift-actions-menu-tooltip"
					overlay={<Tooltip className="shift-actions-tooltip">More actions</Tooltip>}
				>
					<Dropdown.Toggle className="action-menu" />
				</OverlayTrigger>
				<Dropdown.Menu>
					<Dropdown.Item
						className={classNames('action-menu-item', {
							disabled: !item.facility_allow_external_worker_shift_replacement || showRowAsCancelled(item)
						})}
						onClick={() => handleCancelShiftModal(item)}
					>
						<div className="action-menu-item-container">
							<div>Cancel shift</div>
							{!item.facility_allow_external_worker_shift_replacement && (
								<OverlayTrigger
									trigger={['hover']}
									placement="bottom"
									key="shift-actions-menu-item-tooltip"
									overlay={
										<Tooltip show className="shift-actions-tooltip">
											Contact ShiftMed to cancel shifts for this facility
										</Tooltip>
									}
								>
									<div>
										<img src={blockIcon} alt="Action blocked" className="action-menu-item-icon" />
									</div>
								</OverlayTrigger>
							)}
						</div>
					</Dropdown.Item>
				</Dropdown.Menu>
			</Dropdown>
		);
	};

	const _idBodyTemplate = (item) => {
		if (item.contract_id) {
			return (
				<div className="item-id-container">
					<div className="bundle-link" onClick={() => setFilteredBundleId(item.contract_id)}>
						{item.contract_id}
					</div>
					{actionMenuTemplate(item)}
				</div>
			);
		}
		return (
			<div className="item-id-container">
				<div>{item.id}</div>
				{actionMenuTemplate(item)}
			</div>
		);
	};

	const _showNotification = (type, message) => dispatch(showNotification(type, message));

	const clearBundleFilter = () => setFilteredBundleId(null);

	return (
		<>
			<div className="toolbar">
				<button type="button" className="btn refresh-button" onClick={reloadAssignedShifts}>
					<img src={refreshIcon} alt="Refresh" />
					<div className="button-label">Refresh Feed</div>
				</button>
				<div className="flex-one" />
				<FiltersSection
					filterOptions={filterOptions}
					filters={filters}
					updateFilters={updateFilters}
					isDisabled={!!globalFilter.global.value || filteredBundleId}
				/>
			</div>

			<div className="datatable-wrapper">
				<div className="wrapper-container">
					<DataTable
						value={globalFilter.global.value ? assignedShifts : shifts}
						scrollable
						scrollHeight="flex"
						sortMode="multiple"
						selectionMode="single"
						selection={selectedRow}
						dataKey="id"
						virtualScrollerOptions={{ itemSize: 64 }}
						tableStyle={{ minWidth: '100rem' }}
						size="small"
						stripedRows
						cellClassName="table-cell"
						rowClassName={rowClass}
						filters={globalFilter}
					>
						<Column field="start_time" header="Date" style={{ width: '12%' }} sortable body={startDateTemplate} />
						<Column field="start_time_formatted" header="" style={{ width: '0' }} hidden />
						<Column
							field="professional"
							header="Professional"
							sortable
							style={{ width: '12%' }}
							body={professionalTemplate}
						/>
						<Column field="shift_type" header="Shift type" style={{ width: '7%' }} sortable body={shiftTypeTemplate} />
						<Column field="facility_management_company_name" header="Management" style={{ width: '15%' }} sortable />
						<Column field="facility_name" header="Facility" style={{ width: '12%' }} sortable body={facilityTemplate} />
						<Column field="unit" header="Unit" style={{ width: '9%' }} sortable body={unitTemplate} />
						<Column field="start_hour_tz" header="Time" style={{ width: '12%' }} body={timePeriodTemplate} />
						<Column
							field="estimated_pay"
							header="Est. pay"
							style={{ width: '5%' }}
							sortable
							body={estimatedPayTemplate}
						/>
						<Column field="rate" header="Rate" style={{ width: '5%' }} sortable body={rateTemplate} />
						<Column field="facility_city" header="City" style={{ width: '7%' }} sortable body={cityTemplate} />
						<Column field="facility_state_code" header="State" style={{ width: '5%' }} sortable body={stateTemplate} />
						<Column field="facility_zip_code" header="ZIP" style={{ width: '5%' }} sortable body={zipTemplate} />
						<Column
							field={bundleFlagTemplate}
							header="Bundle"
							style={{ width: '4%' }}
							sortable
							body={bundleFlagTemplate}
						/>
						<Column field={idFieldTemplate} header="ID" style={{ width: '5%' }} sortable body={_idBodyTemplate} />
					</DataTable>

					{showCancelShiftModal && (
						<CancelShiftModal
							shift={selectedRow}
							onCancel={() => setShowCancelShiftModal(false)}
							onComplete={onCancelShiftCompleted}
							showNotification={_showNotification}
						/>
					)}
				</div>
			</div>

			{filteredBundleId && !searchTerm && (
				<div className="footer">
					<div>Showing all upcoming shifts in the selected bundle</div>
					<button type="button" className="btn clear-button" onClick={clearBundleFilter}>
						See all assigned shifts
					</button>
				</div>
			)}
		</>
	);
};

export default AgencyAssignedShifts;
